import React from 'react';

import { Button } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { DeleteIcon, ResetIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { IconSize } from 'constants/coreDesignSystem';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { deferredOptions, lienPriorityOptions, sourceOfFundsOptions } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';

import { LIEN_POSITION_MAP } from './AdditionalMortgageForm.constants';
import {
  additionalMortgageBodyWrapperStyles,
  additionalMortgageTitleWrapperStyles,
  getAdditionalMortgageTitleStyles,
} from './AdditionalMortgageForm.styles';

export const additionalFinancingConfigFields = {
  creditorName: {
    fieldId: 'creditorName',
    type: FieldType.INPUT,
    nameInModel: 'creditorName',
    inputProps: {
      label: 'Creditor Name',
      required: true,
      disabled: false,
    },
  },
  amount: {
    fieldId: 'amount',
    type: FieldType.INPUT,
    nameInModel: 'amount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'Amount',
      disabled: false,
      required: true,
    },
  },
  monthlyPayment: {
    fieldId: 'monthlyPayment',
    type: FieldType.INPUT,
    nameInModel: 'monthlyPayment',
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'Monthly Payment',
      disabled: false,
      required: true,
      minValue: 0,
    },
  },
  otherFundDescription: {
    fieldId: 'otherFundDescription',
    type: FieldType.INPUT,
    nameInModel: 'otherFundDescription',
    inputProps: {
      label: 'Description',
      required: false,
      disabled: false,
    },
  },
  monthsDeferred: {
    fieldId: 'monthsDeferred',
    type: FieldType.INPUT,
    nameInModel: 'monthsDeferred',
    inputProps: {
      type: FieldInputType.INT,
      label: 'Months Deferred',
      decimalScale: 0,
    },
  },
  deferredType: {
    fieldId: 'deferredType',
    type: FieldType.SELECT,
    nameInModel: 'deferredType',
    inputProps: {
      options: deferredOptions,
      label: 'Deferred Type',
      disabled: false,
      required: false,
    },
  },
  excludePayment: {
    fieldId: 'excludePayment',
    type: FieldType.CHECKBOX,
    nameInModel: 'excludePayment',
    inputProps: {
      label: 'Payment Excluded',
      className: 'subject-property',
      required: false,
      disabled: false,
    },
  },
  isSubordinate: {
    fieldId: 'isSubordinate',
    type: FieldType.CHECKBOX,
    nameInModel: 'isSubordinate',
    inputProps: {
      label: 'Subordinate',
      required: false,
      disabled: false,
    },
  },
  heloc: {
    fieldId: 'heloc',
    type: FieldType.CHECKBOX,
    nameInModel: 'heloc',
    inputProps: {
      label: 'HELOC',
      required: false,
      disabled: false,
    },
  },
  highLimit: {
    fieldId: 'highLimit',
    type: FieldType.INPUT,
    nameInModel: 'highLimit',
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'High Limit',
      disabled: false,
      required: true,
    },
  },
  isAffordableLoan: {
    fieldId: 'isAffordableLoan',
    type: FieldType.CHECKBOX,
    nameInModel: 'isAffordableLoan',
    inputProps: {
      label: 'Affordable Loan',
      required: false,
      disabled: false,
    },
  },
  lienPriorityType: {
    fieldId: 'lienPriorityType',
    type: FieldType.SELECT,
    nameInModel: 'lienPriorityType',
    inputProps: {
      staticOptions: lienPriorityOptions,
      options: lienPriorityOptions,
      label: 'Lien Priority',
      disabled: false,
      required: true,
    },
  },
  sourceOfFunds: {
    fieldId: 'sourceOfFunds',
    type: FieldType.SELECT,
    nameInModel: 'sourceOfFunds',
    inputProps: {
      options: sourceOfFundsOptions,
      label: 'Source of Funds',
      disabled: false,
      required: true,
    },
  },
};

export const getAdditionalMortgageKey = index => `additional-mortgage-${index}`;

const insertOptionsInAdditionalMortgagesConfigFields = (fields, options) =>
  fields.map(field => ({
    ...field,
    nameInModel: `additionalMortgages[${options.fieldKey}].${field.nameInModel}`,
    inputProps: {
      ...field.inputProps,
      ...getConditionalObject(options.noValidate, {
        noValidate: true,
      }),
    },
  }));

export const getAdditionalMortgageConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    lienPriorityValue,
    mortgageLienPriorityOptions,
    fieldKey,
    numberOfColumns,
    onDelete,
    onRestore,
    isOtherSourceOfFunds,
    isDeferredThreshold,
    isHELOC,
    isNewAdded,
    isDeleted,
    isInitiallyNewAdded,
    isInitiallyDeleted,
    isLienPriorityChanged,
    isLoading,
  }) => {
    const lienPosition = LIEN_POSITION_MAP[lienPriorityValue] ?? 'TBD';

    const isMortgageAdded = isNewAdded || isInitiallyNewAdded;
    const isMortgageDeleted = isDeleted || isInitiallyDeleted;

    const pendingChangesActionType =
      (isMortgageAdded && !isMortgageDeleted && PendingChangesActionType.ADDED) ||
      (isMortgageDeleted && PendingChangesActionType.DELETED) ||
      PendingChangesActionType.NO_CHANGES;

    const additionalMortgageWrapperStyles = getConditionalObject(
      pendingChangesActionType !== PendingChangesActionType.NO_CHANGES,
      {
        borderColor: ({ palette }) => palette.text.highlight,
      },
    );
    const nestedGroupsCustomStyles = {
      gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
      alignItems: 'center',
    };

    const optionsToSetInFields = {
      fieldKey,
      noValidate: isMortgageDeleted,
    };

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            {
              type: FieldType.GROUP,
              key: `${fieldKey}-inputs`,
              customStyles: nestedGroupsCustomStyles,
              fields: insertOptionsInAdditionalMortgagesConfigFields(
                [
                  additionalFinancingConfigFields.creditorName,
                  additionalFinancingConfigFields.amount,
                  additionalFinancingConfigFields.monthlyPayment,
                  {
                    ...additionalFinancingConfigFields.lienPriorityType,
                    inputProps: {
                      ...additionalFinancingConfigFields.lienPriorityType.inputProps,
                      options: mortgageLienPriorityOptions,
                    },
                  },
                  additionalFinancingConfigFields.sourceOfFunds,
                  ...getConditionalArray(isOtherSourceOfFunds, [
                    additionalFinancingConfigFields.otherFundDescription,
                  ]),
                  additionalFinancingConfigFields.monthsDeferred,
                  ...getConditionalArray(isDeferredThreshold, [
                    additionalFinancingConfigFields.deferredType,
                  ]),
                ],
                optionsToSetInFields,
              ),
            },
            {
              type: FieldType.GROUP,
              key: `${fieldKey}-checkboxes`,
              customStyles: nestedGroupsCustomStyles,
              fields: insertOptionsInAdditionalMortgagesConfigFields(
                [
                  additionalFinancingConfigFields.excludePayment,
                  additionalFinancingConfigFields.isSubordinate,
                ],
                optionsToSetInFields,
              ),
            },
            {
              type: FieldType.GROUP,
              key: `${fieldKey}-heloc-related`,
              customStyles: nestedGroupsCustomStyles,
              fields: insertOptionsInAdditionalMortgagesConfigFields(
                [
                  additionalFinancingConfigFields.heloc,
                  ...getConditionalArray(!isHELOC, [
                    additionalFinancingConfigFields.isAffordableLoan,
                  ]),

                  {
                    ...additionalFinancingConfigFields.highLimit,
                    inputProps: {
                      ...additionalFinancingConfigFields.highLimit.inputProps,
                      customStyles: isHELOC ? undefined : { visibility: 'hidden' },
                      noValidate: !isHELOC,
                    },
                  },
                ],
                optionsToSetInFields,
              ),
            },
          ],
          additionalGroupContentConfig: {
            fields: [
              {
                key: `${fieldKey}-additional-button`,
                component: () => (
                  <Button
                    data-testid={PropertyAndLoanTestIds.ADDITIONAL_MORTGAGE_FORM_DELETE_BUTTON}
                    disabled={isLoading || isInitiallyDeleted}
                    size="small"
                    startIcon={
                      isMortgageDeleted ? (
                        <ResetIcon size={IconSize[16]} />
                      ) : (
                        <DeleteIcon size={IconSize[16]} />
                      )
                    }
                    variant="line"
                    onClick={isMortgageDeleted ? onRestore : onDelete}
                  >
                    {isMortgageDeleted ? 'Restore' : 'Remove'}
                  </Button>
                ),
              },
            ],
          },
          key: `${fieldKey}-form`,
          title: {
            label: (
              <PendingChangeWrapper
                pendingChangesActionType={pendingChangesActionType}
                sx={getAdditionalMortgageTitleStyles({
                  isDeleted: isMortgageDeleted,
                  isLienPriorityChanged,
                })}
              >
                <p className="title">
                  Lien Position: <span className="lien-position">{lienPosition}</span>
                </p>
              </PendingChangeWrapper>
            ),
            titleStyles: {
              width: '100%',
            },
            containerStyles: {
              width: '100%',
            },
            wrapperStyles: {
              ...additionalMortgageTitleWrapperStyles,
              ...additionalMortgageWrapperStyles,
            },
          },
          customStyles: {
            ...additionalMortgageBodyWrapperStyles,
            ...additionalMortgageWrapperStyles,
          },
        },
      ],
    };
  },
);
